import React, { useEffect, useMemo, useState } from "react"
import { useNavigate, useNavigation } from "react-router-dom"
import { computed } from "mobx"
import { observer, useLocalObservable } from "mobx-react"
import { Button, Col, Flex, Form, FormInstance } from "antd"
import { useForm } from "antd/es/form/Form"
import { PlusCircleOutlined, PlusOutlined } from "@ant-design/icons"

import { WalletItem } from "@src/app/balanceStore"
import { CardsPayoutFormValue } from "@src/pages/massPayouts/cards/classes/MassPayoutsCardCollection"
import {
  DEFAULT_MANUAL_CARDS_PAYOUTS_FORM,
  DEFAULT_PAYOUT_CARDS_FORM,
} from "@src/pages/massPayouts/cards/ui/constants"
import { MassPayoutCardsFormItem } from "@src/pages/massPayouts/cards/ui/MassPayoutCardsFormItem"
import { SubmitButton } from "@src/shared/componemts/SubmitButton"
import { useCatchFormErrors } from "@src/shared/form/base_error"

type Props = {
  currencies: WalletItem[]
  isLoading?: boolean
  sendFunds: (value: CardsPayoutFormValue) => Promise<void>
}

export const MassPayoutCardsForm = observer(
  ({ currencies, isLoading, sendFunds }: Props) => {
    const [form] = useForm<CardsPayoutFormValue>()
    const formValue = Form.useWatch([], form) ?? {}
    const nav = useNavigate()

    const [formErrors, setErrors] = useState<
      ReturnType<FormInstance["getFieldsError"]>
    >([])

    const { submit } = useCatchFormErrors(
      form,
      async (values: CardsPayoutFormValue) => {
        return sendFunds(values).then((_) => form.resetFields())
      },
    )

    const formAdd = () => {
      form.setFieldValue("payouts", [
        ...form.getFieldValue("payouts"),
        { ...DEFAULT_PAYOUT_CARDS_FORM },
      ])
    }

    const addIsDisabled = useMemo(
      () => Object.values(formValue).some((v) => !v),
      [formValue],
    )

    return (
      <Flex gap={24} style={{ width: "100%" }} vertical>
        <Form
          disabled={isLoading}
          form={form}
          initialValues={DEFAULT_MANUAL_CARDS_PAYOUTS_FORM}
          layout="vertical"
          name="manualPayoutsCard"
          onFinish={submit}
        >
          <Flex gap={24} style={{ width: "100%" }} vertical>
            <Form.List name="payouts">
              {(fields, { remove }) => (
                <>
                  {fields.map((f) => (
                    <MassPayoutCardsFormItem
                      currencies={{
                        isLoading: isLoading,
                        options: currencies,
                      }}
                      field={f}
                      key={f.key}
                      onRemove={remove}
                    />
                  ))}
                </>
              )}
            </Form.List>

            <Flex align="center" justify="flex-end">
              <Flex align="center" gap={16}>
                <Button
                  disabled={isLoading || addIsDisabled}
                  icon={<PlusOutlined />}
                  id="GENERATED_ID_MASSPAYOUTS_BUTTON_ADD_NEW_RECIPIENT_2"
                  onClick={formAdd}
                  size="large"
                  type="primary"
                >
                  Add new recipient
                </Button>

                <SubmitButton
                  buttonProps={{
                    loading: isLoading,
                    size: "large",
                    type: "primary",
                  }}
                  form={form}
                >
                  Pre-Check
                </SubmitButton>
              </Flex>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    )
  },
)
